import PropTypes from "prop-types";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";

FnrErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  /** Custom fallback UI. Defaults to null (renders nothing). */
  fallback: PropTypes.node,
  /** Passed through to react-error-boundary — gets `{ error, resetErrorBoundary }`. Wins over `fallback`. */
  fallbackRender: PropTypes.func,
  /** Additional error handler. Sentry reporting always happens regardless. */
  onError: PropTypes.func,
  /** Optional name to identify this boundary in Sentry (e.g., "NavBar", "ContactsPage") */
  boundaryName: PropTypes.string,
  /** Sent to Sentry as tags. A function is called when the error is caught, so it can read state as of the crash. */
  sentryContext: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

/**
 * Standard error boundary for feature-level components.
 *
 * - Always reports errors to Sentry with component stack
 * - Defaults to rendering nothing (silent UI failure)
 * - Optionally accepts custom fallback UI
 *
 * @example
 * // Silent failure (default) - still reports to Sentry
 * <FnrErrorBoundary boundaryName="UserWidget">
 *   <UserWidget />
 * </FnrErrorBoundary>
 *
 * @example
 * // With custom fallback UI
 * <FnrErrorBoundary fallback={<div>Widget unavailable</div>} boundaryName="UserWidget">
 *   <UserWidget />
 * </FnrErrorBoundary>
 */
export default function FnrErrorBoundary({
  children,
  fallback,
  fallbackRender,
  onError,
  boundaryName,
  sentryContext,
}) {
  const handleError = (error, errorInfo) => {
    const tags =
      typeof sentryContext === "function" ? sentryContext() : sentryContext;
    reportErrorToSentry(error, errorInfo, boundaryName, tags);

    // Call additional handler if provided
    if (onError) {
      onError(error, errorInfo);
    }
  };

  // If no fallback provided, render nothing (silent UI failure)
  // react-error-boundary requires either fallback, fallbackRender, or FallbackComponent
  const renderNothing = fallback === undefined ? () => null : undefined;

  return (
    <ErrorBoundary
      // v3 picks an element fallback over fallbackRender, so drop it here
      fallback={fallbackRender ? undefined : fallback}
      fallbackRender={fallbackRender || renderNothing}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Reports an error to Sentry with component stack context.
 * Safe to call even if Sentry is not loaded.
 */
export function reportErrorToSentry(error, errorInfo, boundaryName, tags) {
  // Always log to console for debugging
  if (boundaryName) {
    console.error(`[${boundaryName}] Error boundary caught:`, error, errorInfo);
  } else {
    console.error("Error boundary caught:", error, errorInfo);
  }

  // Report to Sentry if available
  if (window.Sentry?.captureException) {
    window.Sentry.captureException(error, {
      ...(tags && { tags }),
      extra: {
        ...errorInfo,
        boundaryName: boundaryName || "unnamed",
      },
    });
  }
}
